import { View, Text, ScrollView } from "react-native";
import { Check, Circle } from "lucide-react-native";
import {
  CardSubtitleText,
  CardTitleText,
  Dialog,
  DialogIcon,
  DialogTitleText,
  StatusBadge,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  PHASE_IDS,
  phaseKey,
  type JourneyPath,
} from "@src/packages/cycles/presenters/journeyPhase";
import {
  isMilestoneDone,
  NO_JOURNEY_SIGNALS,
  type JourneySignals,
} from "@src/packages/cycles/presenters/journeyMilestones";
import { EMBER } from "../../../../onboarding/theme";
import { semanticColors, gradients } from "@louise/theme/tokens";
import { LinearGradient } from "expo-linear-gradient";

// Re-exported so the three screens that already import these from here keep
// working; the rules themselves live in the presenter, where they are tested.
export {
  deriveCyclePhase,
  resolveJourneyPath,
  resolveRoadmapPhaseId,
  type JourneyPath,
  type RoadmapPhaseId,
} from "@src/packages/cycles/presenters/journeyPhase";

const roadmapIcon =
  require("../../../../assets/images/you-icons/map-pin.png") as number;

type Props = {
  visible: boolean;
  currentPhaseId: string;
  /**
   * Which journey the last rung describes. Defaults to the preservation
   * wording, which is what every caller said before there was a choice.
   */
  journeyPath?: JourneyPath;
  /**
   * What the app can observe about her journey, which decides the sub-line
   * ticks. Defaults to nothing observed, so a caller that has not wired it up
   * shows an honest empty list rather than inventing progress.
   */
  signals?: JourneySignals;
  onClose: () => void;
};

export default function RoadmapModal({
  visible,
  currentPhaseId,
  journeyPath = "preserve",
  signals = NO_JOURNEY_SIGNALS,
  onClose,
}: Props) {
  const t = useTranslations();
  const currentIndex = PHASE_IDS.findIndex((p) => p === currentPhaseId);
  const phases = PHASE_IDS.map((id) => {
    const k = phaseKey(id, journeyPath);
    return {
      id,
      copyKey: k,
      label: t(`you.modals.roadmap.${k}_label`),
      description: t(`you.modals.roadmap.${k}_description`),
      milestones: [
        t(`you.modals.roadmap.${k}_milestone_1`),
        t(`you.modals.roadmap.${k}_milestone_2`),
        t(`you.modals.roadmap.${k}_milestone_3`),
        t(`you.modals.roadmap.${k}_milestone_4`),
      ],
    };
  });

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        paddingTop: 0,
        paddingHorizontal: 0,
        paddingBottom: 0,
        maxHeight: "100%",
      }}
      backdropTestID="roadmap-dialog-backdrop"
      testID="roadmap-dialog"
    >
      <View style={{ maxHeight: "100%" }}>
        {/* Header */}
        <View
          style={{
            paddingHorizontal: 20,
            paddingTop: 24,
            paddingBottom: 12,
            alignItems: "center",
          }}
        >
          <DialogIcon source={roadmapIcon} width={72} height={72} />
          <DialogTitleText style={{ textAlign: "center", marginTop: 10 }}>
            {t("you.modals.roadmap.title")}
          </DialogTitleText>
        </View>

        {/* Timeline */}
        <ScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ padding: 20, paddingBottom: 24 }}
          showsVerticalScrollIndicator={false}
        >
          {phases.map((phase, i) => {
            const isCompleted = i < currentIndex;
            const isCurrent = i === currentIndex;
            const isUpcoming = i > currentIndex;
            const isLast = i === phases.length - 1;

            return (
              <View
                key={phase.id}
                style={{ flexDirection: "row", marginBottom: isLast ? 0 : 4 }}
              >
                {/* Timeline rail */}
                <View
                  style={{
                    width: 28,
                    alignItems: "center",
                    marginRight: 14,
                  }}
                >
                  {/* Node */}
                  {isCompleted ? (
                    <LinearGradient
                      {...gradients.accent}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Check
                        size={12}
                        color={semanticColors.text.inverse}
                        strokeWidth={3}
                      />
                    </LinearGradient>
                  ) : isCurrent ? (
                    <View
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        borderWidth: 2.5,
                        borderColor: EMBER.colors.warm,
                        backgroundColor: EMBER.colors.surface,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <View
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: EMBER.colors.warm,
                        }}
                      />
                    </View>
                  ) : (
                    <View
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 11,
                        borderWidth: 1.5,
                        borderColor: EMBER.colors.border,
                        backgroundColor: EMBER.colors.surface,
                      }}
                    />
                  )}
                  {/* Connector line */}
                  {!isLast && (
                    <View
                      style={{
                        flex: 1,
                        width: 2,
                        backgroundColor: isCompleted
                          ? EMBER.colors.warm
                          : EMBER.colors.border,
                        marginVertical: 4,
                      }}
                    />
                  )}
                </View>

                {/* Phase content */}
                <View
                  style={{
                    flex: 1,
                    paddingBottom: isLast ? 0 : 24,
                  }}
                >
                  {/* Phase label + badge */}
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <CardTitleText
                      style={
                        isUpcoming ? { color: EMBER.colors.textFaint } : null
                      }
                    >
                      {phase.label}
                    </CardTitleText>
                    {isCurrent && (
                      <StatusBadge
                        label={t("you.modals.roadmap.current_badge")}
                        tone="accent"
                      />
                    )}
                  </View>

                  <CardSubtitleText
                    style={[
                      { marginTop: 0, marginBottom: 10 },
                      isUpcoming ? { color: EMBER.colors.textFaint } : null,
                    ]}
                  >
                    {phase.description}
                  </CardSubtitleText>

                  {/* Milestones */}
                  <View style={{ gap: 6 }}>
                    {phase.milestones.map((milestone, mi) => (
                      <View
                        key={mi}
                        style={{
                          flexDirection: "row",
                          alignItems: "flex-start",
                          gap: 8,
                        }}
                      >
                        {isMilestoneDone(phase.copyKey, mi, signals) ? (
                          <Check
                            size={12}
                            color={EMBER.colors.warm}
                            strokeWidth={2.5}
                            style={{ marginTop: 2 }}
                          />
                        ) : (
                          <Circle
                            size={12}
                            color={
                              isUpcoming
                                ? EMBER.colors.border
                                : EMBER.colors.textFaint
                            }
                            strokeWidth={1.5}
                            style={{ marginTop: 2 }}
                          />
                        )}
                        <Text
                          className="font-dm-sans"
                          style={{
                            flex: 1,
                            fontSize: 12,
                            color: isUpcoming
                              ? EMBER.colors.textFaint
                              : EMBER.colors.textLight,
                            lineHeight: 16,
                            opacity: isUpcoming ? 0.7 : 1,
                          }}
                        >
                          {milestone}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </Dialog>
  );
}
